import React from 'react';
import { fetchPromMetrics, sumBy } from '../../diagnostics/metrics.js';

export default function MetricsSnapshot({ baseUrl }: { baseUrl: string }) {
  const [metrics, setMetrics] = React.useState<{
    scansStarted: number;
    scansOk: number;
    scansErr: number;
    appAccepted: number;
    appThrottled: number;
    appUnauthorized: number;
    withTc: number;
    withGpp: number;
    expStarted: number;
    expCompleted: number;
    receiptsCreated: number;
  } | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        if (!baseUrl) return;
        const samples = await fetchPromMetrics(baseUrl);
        const scansStarted = sumBy(samples, 'cmp_scans_started_total');
        const scansOk = sumBy(samples, 'cmp_scans_completed_total', { status: 'ok' });
        const scansErr = sumBy(samples, 'cmp_scans_completed_total', { status: 'error' });
        const appAccepted = sumBy(samples, 'cmp_append_requests_total', { status: 'accepted' });
        const appThrottled = sumBy(samples, 'cmp_append_requests_total', { status: 'throttled' });
        const appUnauthorized = sumBy(samples, 'cmp_append_requests_total', {
          status: 'unauthorized',
        });
        const withTc = sumBy(samples, 'cmp_append_with_tc_total');
        const withGpp = sumBy(samples, 'cmp_append_with_gpp_total');
        const expStarted = sumBy(samples, 'cmp_exports_started_total');
        const expCompleted = sumBy(samples, 'cmp_exports_completed_total');
        const receiptsCreated = sumBy(samples, 'cmp_receipts_created_total');
        setMetrics({
          scansStarted,
          scansOk,
          scansErr,
          appAccepted,
          appThrottled,
          appUnauthorized,
          withTc,
          withGpp,
          expStarted,
          expCompleted,
          receiptsCreated,
        });
      } catch {
        setMetrics(null);
      }
    })().catch(() => undefined);
  }, [baseUrl]);

  return (
    <div className="rounded-lg border p-4 text-sm">
      <h2 className="text-lg font-semibold mb-2">Service Metrics (snapshot)</h2>
      {!metrics ? (
        <div className="text-sm text-gray-500">Metrics not available.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded border p-3">
            <div className="text-xs text-gray-500">Scans started (total)</div>
            <div className="text-lg font-semibold">{metrics.scansStarted}</div>
          </div>
          <div className="rounded border p-3">
            <div className="text-xs text-gray-500">Scans completed OK</div>
            <div className="text-lg font-semibold">{metrics.scansOk}</div>
          </div>
          <div className="rounded border p-3">
            <div className="text-xs text-gray-500">Scans completed ERR</div>
            <div className="text-lg font-semibold">{metrics.scansErr}</div>
          </div>
          <div className="rounded border p-3">
            <div className="text-xs text-gray-500">Appends accepted</div>
            <div className="text-lg font-semibold">{metrics.appAccepted}</div>
          </div>
          <div className="rounded border p-3">
            <div className="text-xs text-gray-500">Appends throttled</div>
            <div className="text-lg font-semibold">{metrics.appThrottled}</div>
          </div>
          <div className="rounded border p-3">
            <div className="text-xs text-gray-500">Appends unauthorized</div>
            <div className="text-lg font-semibold">{metrics.appUnauthorized}</div>
          </div>
          <div className="rounded border p-3">
            <div className="text-xs text-gray-500">Appends with TCF string</div>
            <div className="text-lg font-semibold">{metrics.withTc}</div>
          </div>
          <div className="rounded border p-3">
            <div className="text-xs text-gray-500">Appends with GPP string</div>
            <div className="text-lg font-semibold">{metrics.withGpp}</div>
          </div>
          <div className="rounded border p-3">
            <div className="text-xs text-gray-500">Receipts created</div>
            <div className="text-lg font-semibold">{metrics.receiptsCreated}</div>
          </div>
          <div className="rounded border p-3">
            <div className="text-xs text-gray-500">Exports completed</div>
            <div className="text-lg font-semibold">{metrics.expCompleted}</div>
            <div className="text-xs text-gray-500 mt-1">
              rate{' '}
              {metrics.expStarted > 0
                ? ((metrics.expCompleted / metrics.expStarted) * 100).toFixed(1) + '%'
                : '—'}
            </div>
          </div>
        </div>
      )}
      <p className="text-xs text-gray-500 mt-2">
        SLO note: target scan error ratio &lt; 1% and append 429 ratio &lt; 0.5% over a rolling 1h.
        See Grafana “CMP Overview” for rates/ratios and alerting.
      </p>
    </div>
  );
}
